import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { SignInScreen } from "../features/auth/SignInScreen";
import { useAuthUser } from "../features/auth/useAuthUser";

/**
 * The dedicated sign-in route (D21).
 *
 * The signed-out event page is no longer a login surface: it carries one CTA
 * and that CTA sends the person here. Two properties make this route the right
 * shape for that handoff. First, `continuePath()` in email-link.ts reads
 * `window.location` at REQUEST time, so a journey started on an eventAVED
 * page returns to `/sign-in?redirect=/e/{slug}&invite=…` when the emailed link
 * is followed — the redirect completes the round trip and the invite the
 * browser carried is still there, pre-filling nothing but still present for
 * the person who needs it again.
 *
 * Second, the redirect survives only our own site. A `redirect` that does not
 * start with a single "/" is dropped — protocol-relative ("//evil.com") and
 * https-prefixed values are absolute URLs an emailed link must never become —
 * and anything that fails falls back to the root, which picks the person's
 * home page, rather than failing the sign-in.
 */
type SignInSearch = { redirect?: string; invite?: string };

function safeRedirect(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  return value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.includes("\\")
    ? value
    : undefined;
}

export const Route = createFileRoute("/sign-in")({
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    redirect: safeRedirect(search.redirect),
    invite: typeof search.invite === "string" ? search.invite : undefined,
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { user, loading } = useAuthUser();
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !loading) {
      // The search param is guaranteed parsed by validateSearch. Without one
      // the root decides where home is (All Events or My Events). Replacing
      // keeps /sign-in out of the back stack.
      void navigate({ to: redirect ?? "/", replace: true });
    }
  }, [user, loading, redirect, navigate]);

  return (
    <SignInScreen tagline="Sign in with your work account or arrange a passwordless link." />
  );
}
